import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  IconButton,
  Chip,
  Pagination,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from '@mui/material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { salesAPI, leadsAPI } from '../../services/api';
import { formatDateInViewerTZ, getViewerTimezone } from '../../utils/dateTimeUtils';
import { toast } from 'react-hot-toast';
import { Delete, Edit, Add, Visibility } from '@mui/icons-material';
import { getCountries, getStates, getCities } from '../../data/locationData';
import { getAvailableTimezones, getTimezone, timezoneNames } from '../../data/timezones';
import { useAuth } from '../../contexts/AuthContext';

const SalesForm = ({ open, onClose, initialData }) => {
  const queryClient = useQueryClient();
  const isEdit = !!initialData;
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    country: '',
    state: '',
    city: '',
    address: '',
    timezone: 'UTC',
    password: '',
    availability_status: 'available',
    countries: [],
    demo_ready: 'No',
    demo_countries: [],
  });

  // State for cascading dropdowns
  const [availableStates, setAvailableStates] = useState([]);
  const [availableCities, setAvailableCities] = useState([]);

  // Update formData when initialData changes or dialog opens
  useEffect(() => {
    if (open) {
      if (initialData) {
        // Edit mode: populate with existing data
        const country = initialData.country || '';
        const state = initialData.state || '';
        const city = initialData.city || '';
        
        // Set available states and cities based on existing data
        if (country) {
          setAvailableStates(getStates(country));
          if (state) {
            setAvailableCities(getCities(country, state));
          }
        }

        setFormData({
          first_name: initialData.first_name || '',
          last_name: initialData.last_name || '',
          email: initialData.email || '',
          phone: initialData.phone || '',
          country: country,
          state: state,
          city: city,
          address: initialData.address || '',
          timezone: initialData.timezone || 'UTC',
          password: '', // Don't populate password in edit mode
          availability_status: initialData.availability_status || 'available',
          countries: initialData.countries || [],
          demo_ready: initialData.demo_ready || 'No',
          demo_countries: initialData.demo_countries || [],
        });
      } else {
        // Create mode: reset to defaults
        setAvailableStates([]);
        setAvailableCities([]);
        setFormData({
          first_name: '',
          last_name: '',
          email: '',
          phone: '',
          country: '',
          state: '',
          city: '',
          address: '',
          timezone: 'UTC',
          password: '',
          availability_status: 'available',
          countries: [],
          demo_ready: 'No',
          demo_countries: [],
        });
      }
    }
  }, [open, initialData]);

  // Handle cascading dropdowns
  const handleCountryChange = (country) => {
    const states = getStates(country);
    const timezone = getTimezone(country);
    setAvailableStates(states);
    setAvailableCities([]); // Clear cities when country changes
    setFormData(prev => ({
      ...prev,
      country,
      state: '', // Clear state when country changes
      city: '', // Clear city when country changes
      timezone: timezone || prev.timezone,
    }));
  };

  const handleStateChange = (state) => {
    const cities = getCities(formData.country, state);
    const timezone = getTimezone(formData.country, state);
    setAvailableCities(cities);
    setFormData(prev => ({
      ...prev,
      state,
      city: '', // Clear city when state changes
      timezone: timezone || prev.timezone,
    }));
  };

  const handleCityChange = (city) => {
    const timezone = getTimezone(formData.country, formData.state, city);
    setFormData(prev => ({
      ...prev,
      city,
      timezone: timezone || prev.timezone,
    }));
  };

  const mutation = useMutation(
    (data) => (isEdit ? salesAPI.update(initialData.id, data) : salesAPI.create(data)),
    {
      onSuccess: () => {
        toast.success(isEdit ? 'Sales user updated' : 'Sales user created');
        queryClient.invalidateQueries('sales');
        onClose();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to save sales user');
      },
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    mutation.mutate(formData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>{isEdit ? 'Edit Sales User' : 'Create Sales User'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth required>
                <InputLabel>Country</InputLabel>
                <Select
                  value={formData.country}
                  onChange={(e) => handleCountryChange(e.target.value)}
                  label="Country"
                >
                  {getCountries().map((country) => (
                    <MenuItem key={country} value={country}>
                      {country}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>State</InputLabel>
                <Select
                  value={formData.state}
                  onChange={(e) => handleStateChange(e.target.value)}
                  label="State"
                  disabled={!formData.country}
                >
                  {availableStates.map((state) => (
                    <MenuItem key={state} value={state}>
                      {state}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>City</InputLabel>
                <Select
                  value={formData.city}
                  onChange={(e) => handleCityChange(e.target.value)}
                  label="City"
                  disabled={!formData.state}
                >
                  {availableCities.map((city) => (
                    <MenuItem key={city} value={city}>
                      {city}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth required>
                <InputLabel>Timezone</InputLabel>
                <Select
                  value={formData.timezone || 'UTC'}
                  onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                  label="Timezone"
                >
                  {getAvailableTimezones().map((tz) => (
                    <MenuItem key={tz} value={tz}>
                      {timezoneNames[tz] || tz}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Address"
                value={formData.address || ''}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Countries Catering To</InputLabel>
                <Select
                  multiple
                  value={formData.countries || []}
                  onChange={(e) => {
                    const value = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                    setFormData({ ...formData, countries: value });
                  }}
                  input={<OutlinedInput label="Countries Catering To" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {getCountries().map((country) => (
                    <MenuItem key={country} value={country}>
                      <Checkbox checked={(formData.countries || []).indexOf(country) > -1} />
                      <ListItemText primary={country} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Select all countries this sales person will be catering to
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Demo Ready</InputLabel>
                <Select
                  value={formData.demo_ready || 'No'}
                  onChange={(e) => setFormData({ ...formData, demo_ready: e.target.value })}
                  label="Demo Ready"
                >
                  <MenuItem value="Yes">Yes</MenuItem>
                  <MenuItem value="No">No</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Yes = this person can be picked as the demo instructor
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Demo Countries</InputLabel>
                <Select
                  multiple
                  value={formData.demo_countries || []}
                  onChange={(e) => {
                    const value = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                    setFormData({ ...formData, demo_countries: value });
                  }}
                  input={<OutlinedInput label="Demo Countries" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {getCountries().map((country) => (
                    <MenuItem key={country} value={country}>
                      <Checkbox checked={(formData.demo_countries || []).indexOf(country) > -1} />
                      <ListItemText primary={country} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {isEdit && (
              <Grid item xs={6}>
                <TextField
                  select
                  fullWidth
                  label="Availability Status"
                  value={formData.availability_status || 'available'}
                  onChange={(e) => setFormData({ ...formData, availability_status: e.target.value })}
                  required
                >
                  <MenuItem value="available">Available</MenuItem>
                  <MenuItem value="unavailable">Unavailable</MenuItem>
                </TextField>
              </Grid>
            )}
            {!isEdit && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  value={formData.password || ''}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={mutation.isLoading}>
            {isEdit ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const Sales = () => {
  const { user } = useAuth();
  const viewerTimezone = getViewerTimezone(user?.timezone);
  const queryClient = useQueryClient();
  const [openForm, setOpenForm] = useState(false);
  const [selected, setSelected] = useState(null);
  const [openView, setOpenView] = useState(false);
  const [viewLeads, setViewLeads] = useState([]);
  const [viewItem, setViewItem] = useState(null);
  const [loadingLeads, setLoadingLeads] = useState(false);

  const [search, setSearch] = useState('');
  const [availability, setAvailability] = useState('');
  const [page, setPage] = useState(1);
  const limit = 10;

  const { data, isLoading } = useQuery(['sales', search, availability, page], () =>
    salesAPI.getAll({
      search,
      availability_status: availability || undefined,
      page,
      limit,
    }).then((res) => res.data)
  );

  const deleteMutation = useMutation((id) => salesAPI.delete(id), {
    onSuccess: () => {
      toast.success('Sales user deleted');
      queryClient.invalidateQueries('sales');
    },
    onError: () => toast.error('Failed to delete sales user'),
  });

  const handleCreate = () => {
    setSelected(null);
    setOpenForm(true);
  };

  const handleEdit = (item) => {
    setSelected(item);
    setOpenForm(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Delete this sales user? Leads will be reassigned per backend rules.')) {
      deleteMutation.mutate(id);
    }
  };

  const handleView = async (item) => {
    setViewItem(item);
    setOpenView(true);
    setLoadingLeads(true);
    try {
      const res = await salesAPI.getById(item.id);
      setViewItem(res.data?.data || res.data || item);
      const leadsResponse = await leadsAPI.getAll({ assigned_to: item.user_id || item.id, limit: 100, page: 1 });
      setViewLeads(leadsResponse.data?.leads || leadsResponse.data || []);
    } catch (err) {
      console.error('Error loading sales user details:', err);
      toast.error('Failed to load sales user details');
      setViewLeads([]);
    } finally {
      setLoadingLeads(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Sales Users</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={handleCreate}>
          New Sales User
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField
          size="small"
          label="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <TextField
          select
          size="small"
          label="Availability"
          value={availability}
          onChange={(e) => setAvailability(e.target.value)}
          sx={{ width: 180 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="available">Available</MenuItem>
          <MenuItem value="unavailable">Unavailable</MenuItem>
        </TextField>
        <Button variant="outlined" onClick={() => { setSearch(''); setAvailability(''); }}>
          Reset
        </Button>
      </Box>

      <Paper>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Showing {data?.pagination ? (data.pagination.total === 0 ? 0 : (page - 1) * limit + 1) : 0}
            {' - '}
            {data?.pagination ? Math.min(page * limit, data.pagination.total) : 0} of {data?.pagination?.total || 0}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Page {page} of {data?.pagination?.pages || 1}
          </Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Location Country</TableCell>
                <TableCell>Countries Catering To</TableCell>
                <TableCell>Leads</TableCell>
                <TableCell>Demo Ready</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={9}>Loading...</TableCell>
                </TableRow>
              )}
              {data?.data?.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    {item.first_name} {item.last_name}
                  </TableCell>
                  <TableCell>{item.email}</TableCell>
                  <TableCell>{item.phone || '-'}</TableCell>
                  <TableCell>{item.country || '-'}</TableCell>
                  <TableCell>
                    {item.countries && item.countries.length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {item.countries.slice(0, 3).map((country) => (
                          <Chip key={country} label={country} size="small" variant="outlined" />
                        ))}
                        {item.countries.length > 3 && (
                          <Chip label={`+${item.countries.length - 3} more`} size="small" variant="outlined" color="default" />
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>{item.assigned_leads_count || 0}</TableCell>
                  <TableCell>
                    <Chip
                      label={item.demo_ready || 'No'}
                      color={item.demo_ready === 'Yes' ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip label={item.availability_status} color={item.availability_status === 'available' ? 'success' : 'default'} size="small" />
                  </TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleView(item)} size="small" title="View">
                      <Visibility />
                    </IconButton>
                    <IconButton onClick={() => handleEdit(item)} size="small" title="Edit">
                      <Edit />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(item.id)} size="small" color="error" title="Delete">
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {!isLoading && (!data?.data || data.data.length === 0) && (
                <TableRow>
                  <TableCell colSpan={9}>No sales users found</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {data?.pagination && data.pagination.pages > 1 && (
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
            <Pagination
              count={data.pagination.pages}
              page={page}
              onChange={(_, val) => setPage(val)}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </Paper>

      <SalesForm open={openForm} onClose={() => setOpenForm(false)} initialData={selected} />

      <Dialog open={openView} onClose={() => setOpenView(false)} maxWidth="md" fullWidth>
        <DialogTitle>Sales User Details</DialogTitle>
        <DialogContent>
          {viewItem && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1">
                {viewItem.first_name} {viewItem.last_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {viewItem.email} {viewItem.phone ? `• ${viewItem.phone}` : ''}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Location: {viewItem.country} {viewItem.city ? `• ${viewItem.city}` : ''} {viewItem.timezone ? `• ${viewItem.timezone}` : ''}
              </Typography>
              {viewItem.countries && viewItem.countries.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'medium', mb: 0.5 }}>
                    Countries Catering To:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {viewItem.countries.map((country) => (
                      <Chip key={country} label={country} size="small" color="primary" variant="outlined" />
                    ))}
                  </Box>
                </Box>
              )}
              <Box sx={{ mt: 1 }}>
                <Chip
                  label={viewItem.availability_status}
                  color={viewItem.availability_status === 'available' ? 'success' : 'default'}
                  size="small"
                />
                <Chip
                  label={`Demo Ready: ${viewItem.demo_ready || 'No'}`}
                  color={viewItem.demo_ready === 'Yes' ? 'success' : 'default'}
                  size="small"
                  sx={{ ml: 0.5 }}
                />
              </Box>
              {viewItem.demo_countries && viewItem.demo_countries.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'medium', mb: 0.5 }}>
                    Demo countries:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {viewItem.demo_countries.map((country) => (
                      <Chip key={country} label={country} size="small" variant="outlined" />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}

          <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
            Assigned Leads
          </Typography>
          {loadingLeads ? (
            <Typography variant="body2">Loading leads...</Typography>
          ) : viewLeads.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No leads assigned.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {viewLeads.map((lead) => (
                  <TableRow key={lead.id}>
                    <TableCell>{`${lead.first_name || ''} ${lead.last_name || ''}`.trim() || '-'}</TableCell>
                    <TableCell>{lead.email || '-'}</TableCell>
                    <TableCell>{lead.phone || '-'}</TableCell>
                    <TableCell>{lead.status || '-'}</TableCell>
                    <TableCell>{lead.created_at ? formatDateInViewerTZ(lead.created_at, viewerTimezone) : '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenView(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Sales;
